import colorPallete, { bodyFonts, elevation, headlineFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { DealerStateContext } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useContext } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

export interface DsgLookAtBranchDisplayStyles {
    eventsCarouselContainer?: InjectableCss;
    titleText?: TypographyPresentationProps;
    carouselSlidesContainer?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => ({});

const enum fields {
    titleAlignment = "titleAlignment",
    lookAtBranchTitle = "lookAtBranchTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.titleAlignment]: string;
        [fields.lookAtBranchTitle]: string;
    };
}

type Props = OwnProps;

export const dsgLookAtBranchDisplayStyles: DsgLookAtBranchDisplayStyles = {
    titleText: {
        variant: "h2",
        weight: headlineFonts.headline2SemiBoldLg.fontWeight,
        css: css`
            text-align: center;
            margin-bottom: 0;
            @media only screen and (max-width: 768px) {
                padding: 0 16px;
            }
        `,
    },
    carouselSlidesContainer: {
        css: css`
            padding: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            padding: 24px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            .branch-details-carousel {
                padding: 16px;
                display: flex;
                flex-direction: column;
                align-items: center;

                img {
                    display: block;
                    margin-bottom: 16px;
                    width: 100%;
                    max-width: 520px;
                    border-radius: 8px;
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                border: 1px solid ${colorPallete.grey300};
                                border-radius: 8px;
                                box-shadow: ${elevation.dp04};
                                margin: 5px;
                                img {
                                    width: 100%;
                                }
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }

            .branch-description {
                font-size: ${bodyFonts.body2RegularLg.fontSize};
                line-height: ${bodyFonts.body2RegularLg.lineHeight};
                text-align: center;
                margin-bottom: 0;
            }
        `,
    },
};

export const ContentWrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
`;

const DsgLookAtBranchDisplay = ({ fields }: Props) => {
    const { value: dealer } = useContext(DealerStateContext);
    if (!dealer) {
        return null;
    }

    const styles = dsgLookAtBranchDisplayStyles;
    const lookAtBranchTitle = fields.lookAtBranchTitle
        ? fields.lookAtBranchTitle?.replace("'BranchName'", dealer.name)
        : "";

    return (
        <StyledWrapper className="branch-location-sliders">
            {dealer?.properties["lookAtEventEditor"]?.trim().length > 0 && (
                <>
                    <StyledWrapper>
                        {lookAtBranchTitle && (
                            <ContentWrapper className="wrapper">
                                <Typography {...styles.titleText}>{lookAtBranchTitle}</Typography>
                            </ContentWrapper>
                        )}
                        <StyledWrapper
                            {...styles.carouselSlidesContainer}
                            data-test-selector="branchImageCarousel_content"
                        >
                            <div className="branch-details-carousel">
                                {parse(dealer?.properties["lookAtEventEditor"], parserOptions)}
                            </div>
                        </StyledWrapper>
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(DsgLookAtBranchDisplay),
    definition: {
        group: "Common",
        displayName: "Look at the branch display",
        fieldDefinitions: [
            {
                name: fields.lookAtBranchTitle,
                displayName: "Look At Branch Title",
                editorTemplate: "TextField",
                defaultValue: "A look at the 'BranchName' Branch",
                fieldType: "Contextual",
                sortOrder: 1,
            },
            {
                name: fields.titleAlignment,
                displayName: "Title Alignment",
                editorTemplate: "RadioButtonsField",
                defaultValue: "center",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,

                options: [
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
            },
        ],
    },
};
export default widgetModule;
